import { io, Socket } from 'socket.io-client';

import BaseAPI from '@lumapps/base-api';
import { get } from '@lumapps/constants';

import { DEFAULT_RECONNECT_TRY_COUNT } from '../constants';

const Config = get();
const wssCell = Config.haussmannCell || '';
const orgId = Config.customerId;

export interface CreateSocketParams {
    apiInstance: BaseAPI;
    socketPath: string;
    maxReconnectTryCount?: number;

    fatalConnectionErrorCallback(err: Error): void;
}

let reconnectTryCount = 0;

const handleAuthError = async (socket: Socket) => {
    /** refresh the auth header */
    await BaseAPI.refreshToken();
    const freshAuthorizationHeader = BaseAPI.getBaseConfig()?.headers?.Authorization || '';

    // We authorize ourselves to reassign some fields of param "socket" as it is an unconventional
    // object that could not be easily deep copy (and that doing so might break things).
    // eslint-disable-next-line no-param-reassign
    socket.io.opts.extraHeaders = {
        ...socket.io.opts.extraHeaders,
        Authorization: typeof freshAuthorizationHeader === 'string' ? freshAuthorizationHeader : '',
    };

    // Same as above (for eslint disabling explanation)
    // eslint-disable-next-line no-param-reassign
    socket.auth = {
        ...socket.auth,
        authorizationHeader: freshAuthorizationHeader,
    };

    socket.close();
    socket.connect();
};

export const createSocket = ({
    apiInstance,
    socketPath,
    maxReconnectTryCount = DEFAULT_RECONNECT_TRY_COUNT,
    fatalConnectionErrorCallback,
}: CreateSocketParams): Socket => {
    const authorizationHeader = BaseAPI.getBaseConfig()?.headers?.Authorization || '';
    const socket = io(wssCell, {
        path: `/${apiInstance.path}/${socketPath}`,
        // Websocket auth passing
        auth: {
            authorizationHeader,
            orgId,
        },
        transports: ['websocket'],
        forceNew: true,
    });

    socket.on('connect_error', async (err: any) => {
        if (err.message === 'not authorized') {
            // Note: chances are that this actually never gets called.
            try {
                if (reconnectTryCount < maxReconnectTryCount) {
                    reconnectTryCount += 1;
                    await handleAuthError(socket);
                } else {
                    fatalConnectionErrorCallback(new Error('Failed to reconnect websockets, try to refresh the page'));
                }
            } catch (err) {
                fatalConnectionErrorCallback(err as Error);
            }
        }
    });

    socket.on('connect', () => {
        reconnectTryCount = 0;
    });

    socket.on('auth_error', async () => {
        await handleAuthError(socket);
    });

    return socket;
};
